import React from "react";
import { X, FileText } from "lucide-react";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import Markdown from "react-markdown";

export default function MarkdownPreviewPanel({ title, content, onClose }) {
    return (
        <div className="h-full w-full flex flex-col bg-white">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <span className="font-medium text-gray-900 truncate">
                        {title || "Markdown Preview"}
                    </span>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Close preview"
                >
                    <X className="h-5 w-5 text-gray-500" />
                </button>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1">
                <div className="p-6 max-w-3xl mx-auto">
                    <Markdown
                        components={{
                            h1: ({ children }) => (
                                <h1 className="text-2xl font-bold mb-4 mt-6 first:mt-0 text-gray-900 border-b border-gray-200 pb-2">
                                    {children}
                                </h1>
                            ),
                            h2: ({ children }) => (
                                <h2 className="text-xl font-semibold mb-3 mt-6 first:mt-0 text-gray-800">
                                    {children}
                                </h2>
                            ),
                            h3: ({ children }) => (
                                <h3 className="text-lg font-semibold mb-2 mt-4 first:mt-0 text-gray-800">
                                    {children}
                                </h3>
                            ),
                            p: ({ children }) => (
                                <p className="mb-4 text-gray-700 leading-relaxed">
                                    {children}
                                </p>
                            ),
                            ul: ({ children }) => (
                                <ul className="list-disc ml-5 mb-4 space-y-1">
                                    {children}
                                </ul>
                            ),
                            ol: ({ children }) => (
                                <ol className="list-decimal ml-5 mb-4 space-y-1">
                                    {children}
                                </ol>
                            ),
                            li: ({ children }) => (
                                <li className="text-gray-700 leading-relaxed">
                                    {children}
                                </li>
                            ),
                            code: (props) => {
                                const { className, children, ...rest } = props;
                                const match = /language-(\w+)/.exec(className || "");
                                const isBlock = !!match;

                                if (isBlock) {
                                    return (
                                        <code
                                            className="block bg-gray-900 text-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto"
                                            {...rest}
                                        >
                                            {children}
                                        </code>
                                    );
                                }

                                return (
                                    <code
                                        className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800"
                                        {...rest}
                                    >
                                        {children}
                                    </code>
                                );
                            },
                            pre: ({ children }) => (
                                <pre className="mb-4 overflow-x-auto">
                                    {children}
                                </pre>
                            ),
                            blockquote: ({ children }) => (
                                <blockquote className="border-l-4 border-blue-300 pl-4 italic text-gray-600 my-4 bg-blue-50 py-2 pr-4 rounded-r">
                                    {children}
                                </blockquote>
                            ),
                            a: ({ href, children }) => (
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                >
                                    {children}
                                </a>
                            ),
                            strong: ({ children }) => (
                                <strong className="font-semibold text-gray-900">
                                    {children}
                                </strong>
                            ),
                            hr: () => (
                                <hr className="my-6 border-gray-200" />
                            ),
                            table: ({ children }) => (
                                <div className="overflow-x-auto mb-4">
                                    <table className="min-w-full border border-gray-200 rounded-lg">
                                        {children}
                                    </table>
                                </div>
                            ),
                            th: ({ children }) => (
                                <th className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-left text-sm font-semibold text-gray-900">
                                    {children}
                                </th>
                            ),
                            td: ({ children }) => (
                                <td className="px-4 py-2 border-b border-gray-100 text-sm text-gray-700">
                                    {children}
                                </td>
                            ),
                        }}
                    >
                        {content}
                    </Markdown>
                </div>
            </ScrollArea>
        </div>
    );
}
